/**
 * @fileoverview Host-side adapter factories for system-web preview.
 * @module system-web/lib/buildPreviewAdapters
 *
 * Responsibilities:
 * - Provide `<InspectionRenderer>` with the host-injected
 *   `AttachmentPicker` and `ResolveContext` it requires.
 * - For preview surfaces, both adapters are LIGHTWEIGHT STUBS — system-web
 *   preview is a read-only smoke surface; real captures + Dexie-backed
 *   lookups land in tenant-web preview / mobile-pwa conduct.
 *
 * Dependencies:
 * - `@firesquirrel/inspection-renderer` for the contract types.
 * - `@firesquirrel/core-domain` for `AttachmentRefLike`.
 *
 * Architecture:
 * - Pure factories. NO Dexie, NO network IO. All preview state lives in
 *   the renderer's PersistAdapter.
 *
 * Notes:
 * - When tenant-web / mobile-pwa cutover lands, they each ship their own
 *   `buildAttachmentPicker.ts` / `buildResolveContext.ts` wired to their
 *   Dexie attachment + entity services. The system-web preview keeps the
 *   stubs here as the simplest possible adapter.
 *
 * @process buildPreviewAttachmentPicker
 * Steps:
 * 1. On capture/pick/captureSignature, synthesize a stable `AttachmentRefLike`
 *    with a deterministic id + a placeholder filename.
 * 2. Resolve immediately (preview is sync).
 * Inputs: none.
 * Outputs: AttachmentPicker.
 * Idempotency: REQUIRED — same parent + counter → same id.
 *
 * @process buildPreviewResolveContext
 * Steps:
 * 1. Resolve any boundField → its placeholder string '[preview value]'.
 * 2. hasModule always true (preview shows everything).
 * 3. resolveLookup returns the static `extras.options` only.
 * Inputs: none.
 * Outputs: ResolveContext.
 * Idempotency: REQUIRED.
 */
import type {
  AttachmentPicker,
  AttachmentParent,
  ResolveContext,
} from '@firesquirrel/inspection-renderer';
import type { AttachmentRefLike } from '@firesquirrel/core-domain';

let attachmentCounter = 0;

function makePreviewRef(parent: AttachmentParent, suffix?: string): AttachmentRefLike {
  attachmentCounter += 1;
  return {
    _id: `preview-att-${attachmentCounter}${suffix ?? ''}`,
    parentType: parent.parentType,
    parentId: parent.parentId,
    kind: parent.kind,
    fileName: `${parent.kind}-${attachmentCounter}.png`,
    uploadedAt: new Date().toISOString(),
  };
}

export function buildPreviewAttachmentPicker(): AttachmentPicker {
  return {
    async capture(parent) {
      return [makePreviewRef(parent, '-camera')];
    },
    async pick(parent, opts) {
      const count = Math.min(opts?.maxCount ?? 1, 3);
      const out: AttachmentRefLike[] = [];
      for (let i = 0; i < count; i++) out.push(makePreviewRef(parent, `-pick-${i}`));
      return out;
    },
    async captureSignature(parent) {
      return makePreviewRef(parent, '-sig');
    },
    async remove() {
      /* preview ignore */
    },
  };
}

export function buildPreviewResolveContext(): ResolveContext {
  return {
    resolveBoundField(args) {
      // Echo the args so the data-bound widget renders something meaningful
      // in preview without requiring a real Dexie record.
      return `[preview ${args.dataSource}.${args.boundField}]`;
    },
    hasModule() {
      return true;
    },
    resolveLookup() {
      return [];
    },
  };
}
